import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Handle, Position } from 'reactflow';
import styled from 'styled-components';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';

import { TYPE_COLORS, EXEC_STATUS_COLORS } from './graphPalette';

const TYPE_ICONS = {
  task: '\u25B6', // ▶
  choice: '\u25C6', // ◆
  parallel: '\u2261', // ≡
  map: '\u2637', // ☷
  pass: '\u2192', // →
  wait: '\u23F1', // ⏱
  succeed: '\u2713', // ✓
  fail: '\u2717', // ✗
};

/* ── execution status styling ── */
const EXEC_STATUS_GLOW = {
  RUNNING: '0 0 0 3px var(--if-info-soft)',
  SUCCEEDED: '0 0 0 3px var(--if-success-soft)',
  FAILED: '0 0 0 3px var(--if-danger-soft)',
};

const EXEC_STATUS_BORDER = EXEC_STATUS_COLORS;

function getExecStyle(data) {
  // Phase 2: per-state status takes priority
  const status = data.stateStatus || data.executionStatus;
  if (!status) return {};
  return {
    boxShadow: EXEC_STATUS_GLOW[status] || 'none',
    borderColor: EXEC_STATUS_BORDER[status] || undefined,
  };
}

/* ── styled ── */
const NodeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${toColorString(color.base.snowWhite)};
  border: 1.5px solid ${({ borderColor }) => borderColor};
  border-radius: 8px;
  padding: 8px 14px;
  min-width: 180px;
  max-width: 220px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  ${({ execStyle }) =>
    execStyle.boxShadow ? `box-shadow: ${execStyle.boxShadow};` : ''}
  ${({ execStyle }) =>
    execStyle.borderColor ? `border-color: ${execStyle.borderColor};` : ''}

  &:hover {
    box-shadow: 0 2px 8px var(--if-shadow);
    border-color: ${toColorString(color.base.charcoal)};
  }
`;

const TypeBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: ${({ bg }) => bg};
  color: var(--if-on-vivid);
  font-size: 13px;
  flex-shrink: 0;
`;

const NodeLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${toColorString(color.base.charcoal)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NodeType = styled.span`
  font-size: 10px;
  color: ${toColorString(color.base.alaskanHusky)};
  margin-top: 1px;
`;

const LabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* ── Start / End node ── */
const CircleNode = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ isEnd }) => (isEnd ? 'var(--if-ink-1)' : TYPE_COLORS.task)};
  border: 2px solid
    ${({ isEnd }) => (isEnd ? 'var(--if-ink-1)' : TYPE_COLORS.succeed)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ isEnd }) =>
    isEnd ? 'var(--if-on-accent)' : 'var(--if-on-vivid)'};
  font-size: 10px;
  font-weight: 700;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

/* ── Component factory ── */
function makeStateNode(stateType) {
  const colour = TYPE_COLORS[stateType] || TYPE_COLORS.task;
  const icon = TYPE_ICONS[stateType] || TYPE_ICONS.task;
  const displayType = stateType.charAt(0).toUpperCase() + stateType.slice(1);

  function StateNode({ data }) {
    const execStyle = getExecStyle(data);
    return (
      <NodeWrapper borderColor={colour} execStyle={execStyle}>
        <Handle
          type="target"
          position={Position.Top}
          style={{ visibility: 'hidden' }}
        />
        <TypeBadge bg={colour}>{icon}</TypeBadge>
        <LabelGroup>
          <NodeLabel title={data.label}>{data.label}</NodeLabel>
          <NodeType>{displayType}</NodeType>
        </LabelGroup>
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ visibility: 'hidden' }}
        />
      </NodeWrapper>
    );
  }

  StateNode.displayName = `SfnNode_${displayType}`;
  StateNode.propTypes = {
    data: PropTypes.shape({
      label: PropTypes.string,
      executionStatus: PropTypes.string,
      stateStatus: PropTypes.string,
    }).isRequired,
  };
  return memo(StateNode);
}

const StartEndNode = memo(({ data }) => {
  const isEnd = data.label === 'End';
  return (
    <CircleNode isEnd={isEnd}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ visibility: 'hidden' }}
      />
      {isEnd ? 'END' : ''}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ visibility: 'hidden' }}
      />
    </CircleNode>
  );
});

StartEndNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};
const sfnNodeTypes = {
  'sfn-task': makeStateNode('task'),
  'sfn-choice': makeStateNode('choice'),
  'sfn-parallel': makeStateNode('parallel'),
  'sfn-map': makeStateNode('map'),
  'sfn-pass': makeStateNode('pass'),
  'sfn-wait': makeStateNode('wait'),
  'sfn-succeed': makeStateNode('succeed'),
  'sfn-fail': makeStateNode('fail'),
  startEnd: StartEndNode,
};

export default sfnNodeTypes;
